import { useState } from "react";
import axios from "axios";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { gbp } from "@/components/build/pickerData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputClass =
  "w-full rounded-xl border border-jade/20 bg-paper/60 px-4 py-3 text-sm text-ink placeholder:text-ink2/40 transition-colors duration-300";

export const SendListDialog = ({ open, onOpenChange, totals }) => {
  const [form, setForm] = useState({ name: "", email: "", note: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = (v) => {
    onOpenChange(v);
    if (!v && status === "sent") setStatus("idle");
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/wishlist`, {
        ...form,
        build: totals.build.id,
        care_plan: totals.plan.id,
        features: totals.picked.map((f) => f.id),
      });
      setStatus("sent");
      setForm({ name: "", email: "", note: "" });
    } catch (err) {
      setError(err?.response?.status === 429 ? "You've sent a few already. Please try again in 15 minutes." : "That didn't send. Please try again in a moment.");
      setStatus("error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md rounded-2xl border-jade/10 bg-paper" data-testid="send-list-dialog">
        {status === "sent" ? (
          <div className="py-6 text-center" data-testid="send-list-success">
            <CheckCircle2 className="mx-auto h-10 w-10 text-jade" />
            <p className="mt-4 font-display text-2xl text-ink">Your list is on its way.</p>
            <p className="mt-2 text-sm text-ink2">I'll read it personally and reply by email to talk it through.</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-medium text-ink">Send my list</DialogTitle>
              <DialogDescription className="text-ink2">
                {totals.build.label} build, {totals.plan.label} care plan
                {totals.picked.length > 0 && `, ${totals.picked.length} feature${totals.picked.length > 1 ? "s" : ""}`}.{" "}
                {gbp(totals.oneOff)}{totals.isQuote && " + quote"} one-off and {gbp(totals.monthly)}/mo.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <input required value={form.name} onChange={update("name")} placeholder="Your name" className={inputClass} data-testid="send-list-name-input" />
              <input required type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" className={inputClass} data-testid="send-list-email-input" />
              <textarea rows={3} value={form.note} onChange={update("note")} placeholder="Anything else I should know? (optional)" className={inputClass} data-testid="send-list-note-input" />
              {status === "error" && (
                <p className="text-sm text-red-700" data-testid="send-list-error">{error}</p>
              )}
              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="send-list-submit-button"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-jade py-3 text-sm font-medium text-paper transition-colors duration-300 hover:bg-ink2 disabled:opacity-60"
              >
                {status === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send my list
              </button>
              <p className="text-center text-xs text-ink2/50">No payment now. This sends your wishlist so we can talk it through together.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
